import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone, Mail, Clock, Hash, ExternalLink, DollarSign, Receipt, ArrowLeft } from 'lucide-react';
import { apiGet, apiPatch, apiPost, ApiError } from '../lib/api';
import { formatTime, formatTimeRange, formatCents, formatDate, todayYmd } from '../lib/format';
import { SkeletonList } from '../components/Skeleton';
import { toast } from '../lib/toast';
import { useTenant } from '../lib/tenantContext';
import { IntakeFieldDisplay } from '../components/IntakeFieldInput';
import { PaymentModal, type PaymentMethod } from '../components/PaymentModal';
import { EditAppointmentModal } from '../components/EditAppointmentModal';

interface Booking {
  id: number;
  start_at: string;
  end_at: string;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  source: 'walk_in' | 'phone' | 'online';
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  service_id: number;
  service_name: string;
  duration_min: number;
  price_cents: number;
  color: string | null;
  operator_notes: string | null;
  intake_data_json: string;
  amount_collected_cents: number | null;
  payment_method: string | null;
  paid_at: string | null;
  checked_in_at: string | null;
  station_id: number | null;
  station_name: string | null;
  station_color: string | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
}

interface CustomerDetail {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  metadata_json: string;
  total_visits: number;
  last_visit_at: string | null;
}

const STATUS_STYLE: Record<Booking['status'], string> = {
  pending:     'bg-yellow-500/15 text-yellow-200',
  confirmed:   'bg-purple-500/15 text-purple-200',
  in_progress: 'bg-sky-500/15 text-sky-200',
  completed:   'bg-emerald-500/15 text-emerald-200',
  cancelled:   'bg-zinc-500/15 text-zinc-300',
  no_show:     'bg-rose-500/15 text-rose-200',
};

const STATUS_LABEL: Record<Booking['status'], string> = {
  pending:     'pending',
  confirmed:   'confirmed',
  in_progress: 'in shop',
  completed:   'completed',
  cancelled:   'cancelled',
  no_show:     'no-show',
};

// "checked in 23 min ago"
function minutesAgo(ts: string): number {
  const t = new Date(ts.replace(' ', 'T') + 'Z').getTime();
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

const SOURCE_LABEL: Record<Booking['source'], string> = {
  walk_in: 'Walk-in',
  phone:   'Phone',
  online:  'Online',
};

// "2026-05-14" → "Thu, May 14" (local, no UTC shift)
function formatYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function Today() {
  const [searchParams] = useSearchParams();
  const today = todayYmd();
  const date = searchParams.get('date') || today;
  const isToday = date === today;
  const [items, setItems] = useState<Booking[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [customerCache, setCustomerCache] = useState<Record<number, CustomerDetail>>({});
  const [paymentFor, setPaymentFor] = useState<Booking | null>(null);
  const [resendBusy, setResendBusy] = useState<number | null>(null);
  const [editingBookingId, setEditingBookingId] = useState<number | null>(null);

  // Today's revenue rollup — sum amount_collected_cents over completed bookings.
  // Falls back to service price_cents while paid_at is null (legacy rows or
  // bookings completed before money tracking shipped). Pre-completed bookings
  // are NOT counted; only what's actually been paid is "today's money."
  const revenue = useMemo(() => {
    if (!items) return { cents: 0, count: 0 };
    let cents = 0;
    let count = 0;
    for (const b of items) {
      if (b.status !== 'completed') continue;
      cents += b.amount_collected_cents ?? b.price_cents;
      count++;
    }
    return { cents, count };
  }, [items]);

  async function load() {
    try {
      const r = await apiGet<{ bookings: Booking[] }>(`/api/bookings?date=${date}`);
      setItems(r.bookings.filter((b) => b.status !== 'cancelled'));
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => { load(); }, [date]);

  async function toggleExpand(b: Booking) {
    const next = expanded === b.id ? null : b.id;
    setExpanded(next);
    // Lazy-load full customer record on first expand
    if (next === b.id && b.customer_id && !customerCache[b.customer_id]) {
      try {
        const r = await apiGet<{ customer: CustomerDetail }>(`/api/customers/${b.customer_id}`);
        setCustomerCache((prev) => ({ ...prev, [b.customer_id!]: r.customer }));
      } catch {
        // silent — collapsed state still works
      }
    }
  }

  async function setStatus(id: number, status: Booking['status']) {
    try {
      await apiPatch(`/api/bookings/${id}`, { status });
      const label =
        status === 'completed' ? 'Marked complete.' :
        status === 'no_show'   ? 'Marked no-show.'  :
        status === 'confirmed' ? 'Confirmed — email sent if address on file.' :
        `Status: ${status.replace('_', ' ')}.`;
      toast.success(label);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function completeWithPayment(
    id: number,
    args: { amount_collected_cents: number; payment_method: PaymentMethod }
  ) {
    try {
      await apiPatch(`/api/bookings/${id}`, {
        status: 'completed',
        amount_collected_cents: args.amount_collected_cents,
        payment_method: args.payment_method,
      });
      toast.success(`Collected ${formatCents(args.amount_collected_cents)} via ${args.payment_method}.`);
      setPaymentFor(null);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function resendConfirmation(b: Booking) {
    setResendBusy(b.id);
    try {
      await apiPost(`/api/bookings/${b.id}/resend-confirmation`, {});
      toast.success('Confirmation email resent');
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        toast.error('Resend too soon — try again in a moment');
      } else if (e instanceof ApiError && e.status === 400) {
        toast.error("Can't resend — no email on file");
      } else {
        toast.error((e as Error).message);
      }
    } finally {
      // Keep "Sent ✓" visible for 3s before re-enabling the link.
      setTimeout(() => setResendBusy(null), 3000);
    }
  }

  // One-tap "Email receipt" from a completed booking. Generates the invoice
  // (or returns the existing one) then sends. Both calls hit the API.
  async function sendReceipt(bookingId: number) {
    try {
      const r1 = await apiPost<{ invoice: { id: number }; already_existed?: boolean }>(
        `/api/invoices/from-booking/${bookingId}`,
        {}
      );
      await apiPost(`/api/invoices/${r1.invoice.id}/send`, {});
      toast.success(r1.already_existed ? 'Receipt re-sent.' : 'Receipt sent.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  if (err) return <div className="text-rose-400">{err}</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{isToday ? 'Today' : 'Schedule'}</h1>
          <p className="text-muted text-sm">
            {isToday
              ? new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
              : formatYmd(date)}
          </p>
        </div>
        {!isToday && (
          <Link
            to="/"
            className="btn-ghost px-3 py-2 text-sm inline-flex items-center gap-1.5 shrink-0"
          >
            <ArrowLeft size={14} /> Today
          </Link>
        )}
      </div>

      {/* Revenue rollup — visible only once at least one booking is completed */}
      {items && revenue.count > 0 && (
        <div className="card flex items-center justify-between border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-300">
              <DollarSign size={18} />
            </span>
            <div>
              <div className="text-2xl font-mono font-bold tracking-tight">{formatCents(revenue.cents)}</div>
              <div className="text-xs text-muted">{isToday ? 'collected today' : 'collected'}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-base font-mono font-semibold">{revenue.count}</div>
            <div className="text-xs text-muted">complete</div>
          </div>
        </div>
      )}

      {!items && <SkeletonList count={3} variant="card" />}

      {items && items.length === 0 && (
        <div className="card text-center py-8">
          <p className="text-muted">
            {isToday ? 'Nothing on the books today.' : `Nothing on the books for ${formatYmd(date)}.`}
          </p>
          <Link to="/new-booking" className="btn-primary inline-flex mt-4">+ New booking</Link>
        </div>
      )}

      {/* In-shop section — pinned at top with a live pulsing indicator. */}
      {items && items.some((b) => b.status === 'in_progress') && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mt-1">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-sky-400 opacity-60 animate-ping"></span>
              <span className="absolute inset-0 rounded-full bg-sky-400"></span>
            </span>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-sky-300">
              In Shop &middot; {items.filter((b) => b.status === 'in_progress').length}
            </h2>
          </div>
          {items
            .filter((b) => b.status === 'in_progress')
            .map((b) => (
              <BookingCard
                key={b.id}
                b={b}
                expanded={expanded === b.id}
                onToggle={() => toggleExpand(b)}
                onStatus={(s) => {
                  if (s === 'completed') setPaymentFor(b);
                  else setStatus(b.id, s);
                }}
                onSendReceipt={() => sendReceipt(b.id)}
                onResendConfirmation={() => resendConfirmation(b)}
                onEdit={() => setEditingBookingId(b.id)}
                resendBusy={resendBusy === b.id}
                customer={b.customer_id ? customerCache[b.customer_id] : undefined}
              />
            ))}
          <div className="border-t border-border my-2"></div>
        </div>
      )}

      {/* Everything else, in time order */}
      {items
        ?.filter((b) => b.status !== 'in_progress')
        .map((b) => (
          <BookingCard
            key={b.id}
            b={b}
            expanded={expanded === b.id}
            onToggle={() => toggleExpand(b)}
            onStatus={(s) => {
              if (s === 'completed') setPaymentFor(b);
              else setStatus(b.id, s);
            }}
            onSendReceipt={() => sendReceipt(b.id)}
            onResendConfirmation={() => resendConfirmation(b)}
            onEdit={() => setEditingBookingId(b.id)}
            resendBusy={resendBusy === b.id}
            customer={b.customer_id ? customerCache[b.customer_id] : undefined}
          />
        ))}

      <PaymentModal
        open={paymentFor !== null}
        defaultAmountCents={paymentFor?.price_cents ?? 0}
        onConfirm={(args) => paymentFor && completeWithPayment(paymentFor.id, args)}
        onClose={() => setPaymentFor(null)}
      />

      {editingBookingId !== null && items && (() => {
        const b = items.find((x) => x.id === editingBookingId);
        if (!b) return null;
        return (
          <EditAppointmentModal
            bookingId={b.id}
            currentServiceId={b.service_id}
            currentStartAt={b.start_at}
            onClose={() => setEditingBookingId(null)}
            onSaved={() => load()}
          />
        );
      })()}
    </div>
  );
}

function BookingCard({
  b,
  expanded,
  onToggle,
  onStatus,
  onSendReceipt,
  onResendConfirmation,
  onEdit,
  resendBusy,
  customer,
}: {
  b: Booking;
  expanded: boolean;
  onToggle: () => void;
  onStatus: (s: Booking['status']) => void;
  onSendReceipt: () => void;
  onResendConfirmation: () => void;
  onEdit: () => void;
  resendBusy: boolean;
  customer?: CustomerDetail;
}) {
  const { tenant } = useTenant();
  const customerFields = tenant?.customer_fields ?? [];
  const bookingFields = tenant?.booking_fields ?? [];

  let intake: Record<string, unknown> = {};
  try { intake = JSON.parse(b.intake_data_json || '{}'); } catch {}

  let customerMeta: Record<string, unknown> = {};
  try { customerMeta = JSON.parse(customer?.metadata_json || '{}'); } catch {}

  const hasIntake = bookingFields.some((f) => intake[f.key] !== undefined && intake[f.key] !== '');
  const hasCustomerMeta = customer && customerFields.some(
    (f) => customerMeta[f.key] !== undefined && customerMeta[f.key] !== ''
  );

  return (
    <div
      className="card flex flex-col gap-2 border-l-4 cursor-pointer select-none"
      style={{ borderLeftColor: b.color || 'var(--accent)' }}
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg font-semibold truncate">
            {b.customer_name || <span className="text-muted">No customer</span>}
          </div>
          <div className="text-sm text-muted">{b.service_name}</div>
          {b.customer_phone && (
            <a
              href={`tel:${b.customer_phone}`}
              className="inline-flex items-center gap-1.5 text-sm font-mono text-accent mt-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone size={13} />
              {b.customer_phone}
            </a>
          )}
          {(b.station_name || b.assigned_to_name) && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {b.station_name && (
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: b.station_color || 'var(--accent)' }} />
                  <span className="text-muted">{b.station_name}</span>
                </span>
              )}
              {b.assigned_to_name && (
                <span className="text-xs text-muted">· {b.assigned_to_name}</span>
              )}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-mono font-semibold">{formatTime(b.start_at)}</div>
          <div className="text-xs font-mono text-muted">{formatTimeRange(b.start_at, b.end_at)}</div>
          <div className="text-xs font-mono text-muted">{formatCents(b.price_cents)}</div>
        </div>
      </div>

      {b.operator_notes && !expanded && (
        <p className="text-sm text-text/90 bg-panel-2 rounded p-2 line-clamp-2">📝 {b.operator_notes}</p>
      )}

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pt-1">
              {/* Notes (full, not clamped) */}
              {b.operator_notes && (
                <p className="text-sm text-text/90 bg-panel-2 rounded p-2 whitespace-pre-wrap">
                  📝 {b.operator_notes}
                </p>
              )}

              {/* Customer metadata (schema-driven) */}
              {hasCustomerMeta && (
                <div className="bg-panel-2 rounded p-2 space-y-1 text-sm">
                  <div className="text-xs uppercase tracking-wide text-muted mb-1">Customer</div>
                  {customerFields.map((f) => (
                    <IntakeFieldDisplay key={f.key} field={f} value={customerMeta[f.key]} />
                  ))}
                </div>
              )}

              {/* Booking intake */}
              {hasIntake && (
                <div className="bg-panel-2 rounded p-2 space-y-1 text-sm">
                  <div className="text-xs uppercase tracking-wide text-muted mb-1">Intake</div>
                  {bookingFields.map((f) => (
                    <IntakeFieldDisplay key={f.key} field={f} value={intake[f.key]} />
                  ))}
                </div>
              )}

              {/* Customer email + visit history (when loaded) */}
              {customer && (customer.email || customer.total_visits > 0) && (
                <div className="space-y-1 text-sm">
                  {customer.email && (
                    <a
                      href={`mailto:${customer.email}`}
                      className="inline-flex items-center gap-1.5 text-accent font-mono"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Mail size={13} />
                      {customer.email}
                    </a>
                  )}
                  {customer.total_visits > 0 && (
                    <div className="text-muted">
                      <span className="font-mono">{customer.total_visits}</span> previous visit
                      {customer.total_visits === 1 ? '' : 's'}
                      {customer.last_visit_at && <> · last {formatDate(customer.last_visit_at)}</>}
                    </div>
                  )}
                </div>
              )}

              {/* Booking metadata (small) */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                <span className="inline-flex items-center gap-1">
                  <Hash size={11} /> <span className="font-mono">{b.id}</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  {SOURCE_LABEL[b.source]}
                </span>
                <span className="inline-flex items-center gap-1 font-mono">
                  <Clock size={11} /> {b.duration_min} min
                </span>
              </div>

              {/* Customer link */}
              {b.customer_id && (
                <Link
                  to={`/customers/${b.customer_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-2"
                >
                  View customer profile <ExternalLink size={13} />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`pill ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>
          {b.status === 'in_progress' && b.checked_in_at && (
            <span className="text-xs text-sky-300/80 font-mono">
              · in for {minutesAgo(b.checked_in_at)} min
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Pending only → Confirm button (sends confirmation email) */}
          {b.status === 'pending' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatus('confirmed'); }}
              className="px-3 py-2 text-sm rounded-xl font-semibold bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 transition active:scale-95"
            >
              ✓ Confirm
            </button>
          )}
          {/* Confirmed / pending → 'Check in' button (drops off the dog) */}
          {(b.status === 'pending' || b.status === 'confirmed') && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatus('in_progress'); }}
              className="px-3 py-2 text-sm rounded-xl font-semibold bg-sky-500/20 text-sky-200 hover:bg-sky-500/30 transition active:scale-95"
            >
              ✓ Check in
            </button>
          )}
          {/* Edit button — not for completed/cancelled/no_show */}
          {b.status !== 'completed' && b.status !== 'cancelled' && b.status !== 'no_show' && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="btn-ghost px-3 py-2 text-sm"
            >
              Edit
            </button>
          )}
          {/* In-shop or already-confirmed → 'Done' (opens payment modal) */}
          {(b.status === 'in_progress' || b.status === 'confirmed' || b.status === 'pending') && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatus('completed'); }}
              className="btn-success px-3 py-2 text-sm"
            >
              ✓ Done
            </button>
          )}
          {/* Already completed → 'Email receipt' (one-tap, idempotent) */}
          {b.status === 'completed' && b.amount_collected_cents != null && (
            <button
              onClick={(e) => { e.stopPropagation(); onSendReceipt(); }}
              className="btn-ghost px-3 py-2 text-sm inline-flex items-center gap-1.5"
              title="Email receipt to customer"
            >
              <Receipt size={13} />
              Receipt
            </button>
          )}
          {b.status !== 'no_show' && b.status !== 'completed' && b.status !== 'cancelled' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatus('no_show'); }}
              className="btn-ghost px-3 py-2 text-sm"
            >
              No-show
            </button>
          )}
          <ChevronDown
            size={18}
            className={`text-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
        {b.status === 'confirmed' && b.customer_email && (
          <div className="mt-2">
            <button
              onClick={(e) => { e.stopPropagation(); onResendConfirmation(); }}
              disabled={resendBusy}
              className="text-xs text-muted hover:text-text underline disabled:opacity-50 disabled:no-underline"
            >
              {resendBusy ? 'Sent ✓' : '↳ Resend confirmation email'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
